import React from 'react';
import { Thermometer, Droplets, Zap, Sun, Users, AlertTriangle } from 'lucide-react';

export interface SensorDisplayStyle04Props {
  type?: 'temperature' | 'humidity' | 'power' | 'lux' | 'occupancy';
  value?: number | boolean;
  label?: string;
  unit?: string;
  size?: 'small' | 'medium' | 'large';
  isWaterTankFull?: boolean;
  className?: string;
}

const SENSOR_CONFIG: Record<string, {
  icon: React.ElementType;
  color: string;
  bgGradient: string;
  borderColor: string;
  unit: string;
  label: string;
  formatValue: (v: any) => string;
}> = {
  temperature: {
    icon: Thermometer,
    color: 'text-orange-400',
    bgGradient: 'from-orange-900/30 to-slate-900',
    borderColor: 'border-orange-500/50',
    unit: '°C',
    label: '溫度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  humidity: {
    icon: Droplets,
    color: 'text-blue-400',
    bgGradient: 'from-slate-800 to-slate-900',
    borderColor: 'border-slate-700/50',
    unit: '%',
    label: '濕度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  power: {
    icon: Zap,
    color: 'text-yellow-400',
    bgGradient: 'from-yellow-900/30 to-slate-900',
    borderColor: 'border-yellow-500/50',
    unit: 'W',
    label: '功率',
    formatValue: (v) => {
      if (typeof v !== 'number') return '--';
      return v >= 1000 ? (v / 1000).toFixed(2) : v.toFixed(0);
    }
  },
  voltage: {
    icon: Zap,
    color: 'text-yellow-300',
    bgGradient: 'from-yellow-900/30 to-slate-900',
    borderColor: 'border-yellow-400/50',
    unit: 'V',
    label: '電壓',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  current: {
    icon: Zap,
    color: 'text-orange-300',
    bgGradient: 'from-orange-900/30 to-slate-900',
    borderColor: 'border-orange-400/50',
    unit: 'A',
    label: '電流',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  energy: {
    icon: Zap,
    color: 'text-green-400',
    bgGradient: 'from-green-900/30 to-slate-900',
    borderColor: 'border-green-500/50',
    unit: 'kWh',
    label: '累積用電',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  lux: {
    icon: Sun,
    color: 'text-amber-400',
    bgGradient: 'from-amber-900/30 to-slate-900',
    borderColor: 'border-amber-500/50',
    unit: 'lux',
    label: '光照',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  occupancy: {
    icon: Users,
    color: 'text-purple-400',
    bgGradient: 'from-purple-900/30 to-slate-900',
    borderColor: 'border-purple-500/50',
    unit: '',
    label: '佔用狀態',
    formatValue: (v) => v ? '有人' : '無人'
  }
};

/**
 * SensorDisplay Style 04 - Circle
 * Large circular display with animated icon background
 * Uses type parameter to determine icon and color
 */
export const Style04_Circle: React.FC<SensorDisplayStyle04Props> = ({
  type = 'temperature',
  value,
  label,
  unit,
  size = 'large',
  isWaterTankFull = false,
  className = ''
}) => {
  const sizeConfig = {
    small: { container: 'w-24 h-24', value: 'text-2xl', label: 'text-xs', icon: 'w-4 h-4', unitSize: 'text-lg' },
    medium: { container: 'w-32 h-32', value: 'text-4xl', label: 'text-sm', icon: 'w-6 h-6', unitSize: 'text-xl' },
    large: { container: 'w-40 h-40', value: 'text-5xl', label: 'text-sm', icon: 'w-8 h-8', unitSize: 'text-2xl' }
  };

  const sizeConf = sizeConfig[size];
  const config = SENSOR_CONFIG[type] || SENSOR_CONFIG.temperature;
  const Icon = config.icon;
  const displayLabel = label ?? config.label;
  const displayUnit = unit ?? config.unit;
  const displayValue = config.formatValue(value);

  // Override colors if water tank is full (for humidity/dehumidifier)
  const iconColor = isWaterTankFull ? 'text-orange-400' : config.color;
  const borderColor = isWaterTankFull ? 'border-orange-500/50' : config.borderColor;
  const bgGradient = isWaterTankFull 
    ? 'from-orange-900/30 to-slate-900' 
    : config.bgGradient;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className={`${sizeConf.container} rounded-full bg-gradient-to-br ${bgGradient} border ${borderColor} flex flex-col items-center justify-center relative overflow-hidden`}>
        {/* Water tank full warning indicator */}
        {isWaterTankFull && (
          <div className="absolute top-3 right-3 z-20">
            <div className="relative">
              <AlertTriangle className="w-5 h-5 text-orange-400 animate-pulse" />
            </div>
          </div>
        )}

        {/* Animated icons background */}
        <div className="absolute inset-0 flex items-end justify-center opacity-30">
          <div className="flex gap-1 mb-4">
            {isWaterTankFull ? (
              <>
                <Droplets className={`w-4 h-4 ${iconColor}`} />
                <Droplets className={`w-5 h-5 ${iconColor}`} />
                <Droplets className={`w-4 h-4 ${iconColor}`} />
              </>
            ) : (
              <>
                <Icon className={`w-3 h-3 ${iconColor} animate-bounce`} style={{ animationDelay: '0ms' }} />
                <Icon className={`w-4 h-4 ${iconColor} animate-bounce`} style={{ animationDelay: '150ms' }} />
                <Icon className={`w-3 h-3 ${iconColor} animate-bounce`} style={{ animationDelay: '300ms' }} />
              </>
            )}
          </div>
        </div>
        
        {/* Value display */}
        <div className="relative z-10 flex items-baseline">
          <span className={`${sizeConf.value} font-bold text-white`}>{displayValue}</span>
          {displayUnit && (
            <span className={`${sizeConf.unitSize} text-slate-400 ml-1`}>{displayUnit}</span>
          )}
        </div>
      </div>
      
      {/* Label below - show warning text if water tank full */}
      {isWaterTankFull ? (
        <p className={`${sizeConf.label} text-orange-400 mt-3 font-medium flex items-center gap-1`}>
          <AlertTriangle className="w-3 h-3" />
          水箱已滿
        </p>
      ) : (
        <p className={`${sizeConf.label} text-slate-400 mt-3`}>{displayLabel}</p>
      )}
    </div>
  );
};

export default Style04_Circle;
